import React from 'react';

// Helper to display selected add-ons from Step 2
const renderSelectedAddOns = (addOnsData, allAddOnsConfig) => {
  if (!addOnsData || Object.keys(addOnsData).length === 0) return null;
  const selected = allAddOnsConfig.filter(addon => addOnsData[addon.key]);
  if (selected.length === 0) return null;

  return (
    <ul className="list-disc list-inside text-sm text-brand-text-light pl-4">
      {selected.map(addon => <li key={addon.key}>{addon.name} ({addon.price})</li>)}
    </ul>
  );
};

// Placeholder: You'll need to pass the actual add-on configurations here
// from where they are defined (e.g., StepTwoServiceDetails or a shared config)
const REPAIR_ADDONS_CONFIG = [
    { name: "Capacitor Replacement", price: "Approx. $150 - $300", key: "capacitor" },
    { name: "System Tune-Up & Cleaning", price: "$99", key: "tuneUp" },
    { name: "Smart Thermostat Installation", price: "Starting from $250", key: "smartThermostat" }
];
const INSTALLATION_ADDONS_CONFIG = [
    { name: "Smart Thermostat Upgrade", price: "+$150", key: "smartThermostatUpgrade" },
    { name: "Whole-Home Air Purifier", price: "+$800", key: "airPurifier" },
    { name: "Extended Warranty Plan (10 Year Labor)", price: "+$500", key: "extendedWarranty" }
];

const StepFiveReviewConfirm = ({ onNext, onPrevious, formData, goToStep, isSubmitting }) => {
  const { 
    serviceType, 
    stepTwoDetails,
    stepThreeContactInfo,
    stepFourSchedule 
  } = formData;

  const isRepairType = serviceType && serviceType.toLowerCase().includes('repair');
  const isInstallationType = serviceType && serviceType.toLowerCase().includes('installation');

  const getTierNameByKey = (key) => {
    const tiers = [
        { name: "Standard Efficiency", key: "standard" },
        { name: "High Performance", key: "performance" },
        { name: "Premium Comfort & Efficiency", key: "premium" }
    ];
    const found = tiers.find(t => t.key === key);
    return found ? found.name : key;
  }

  const handleSubmit = () => {
    // Here, you would typically call an API submission function passed via props
    // For now, we'll just call the onNext which might trigger a success view or close modal
    onNext(); 
  };

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold text-brand-text-dark mb-4">Review & Confirm Your Service Request</h3>
      
      {/* Service Details Section */}
      <div className="p-4 border border-gray-200 rounded-md bg-gray-50">
        <div className="flex justify-between items-center mb-2">
          <h4 className="text-lg font-semibold text-brand-green-medium">Service Details</h4>
          <button onClick={() => goToStep(2)} className="text-sm text-brand-accent-orange hover:underline">Edit</button>
        </div>
        <p className="text-sm"><strong className="text-brand-text-light">Service Category:</strong> {serviceType}</p>
        
        {stepTwoDetails && (
          <div className="mt-2 space-y-1">
            {isRepairType && stepTwoDetails.commonIssues && stepTwoDetails.commonIssues.length > 0 && (
              <p className="text-sm"><strong className="text-brand-text-light">Problems Reported:</strong> {stepTwoDetails.commonIssues.join(', ')}</p>
            )}
            {isRepairType && stepTwoDetails.otherDescription && (
              <p className="text-sm"><strong className="text-brand-text-light">Other Details:</strong> {stepTwoDetails.otherDescription}</p>
            )}
            {isRepairType && <p className="text-sm font-medium mt-1 text-brand-green-dark">Diagnostic Fee: ${formData.stepTwoDetails?.DIAGNOSTIC_FEE || '89.00'} (waived if repairs proceed)</p>}
            
            {isInstallationType && stepTwoDetails.installationType && (
              <p className="text-sm"><strong className="text-brand-text-light">Installation Type:</strong> {stepTwoDetails.installationType}</p>
            )}
            {isInstallationType && stepTwoDetails.propertySize && (
              <p className="text-sm"><strong className="text-brand-text-light">Property Size:</strong> {stepTwoDetails.propertySize}</p>
            )}
            {isInstallationType && stepTwoDetails.propertyType && (
              <p className="text-sm"><strong className="text-brand-text-light">Property Type:</strong> {stepTwoDetails.propertyType}</p>
            )}
            {isInstallationType && stepTwoDetails.selectedTier && (
              <p className="text-sm"><strong className="text-brand-text-light">Selected System Tier:</strong> {getTierNameByKey(stepTwoDetails.selectedTier)} ({formData.stepTwoDetails?.systemTiers?.find(t=>t.key === stepTwoDetails.selectedTier)?.price})</p>
            )}

            {isRepairType && stepTwoDetails.addOns && Object.keys(stepTwoDetails.addOns).length > 0 && (
                <div className="mt-1">
                    <p className="text-sm"><strong className="text-brand-text-light">Selected Add-ons:</strong></p>
                    {renderSelectedAddOns(stepTwoDetails.addOns, REPAIR_ADDONS_CONFIG)}
                </div>
            )}
            {isInstallationType && stepTwoDetails.addOns && Object.keys(stepTwoDetails.addOns).length > 0 && (
                <div className="mt-1">
                    <p className="text-sm"><strong className="text-brand-text-light">Selected Add-ons:</strong></p>
                    {renderSelectedAddOns(stepTwoDetails.addOns, INSTALLATION_ADDONS_CONFIG)}
                </div>
            )}
            {!isRepairType && !isInstallationType && stepTwoDetails.generalNotes && (
                 <p className="text-sm"><strong className="text-brand-text-light">Notes:</strong> {stepTwoDetails.generalNotes}</p>
            )}
          </div>
        )}
      </div>

      {/* Your Information Section */}
      {stepThreeContactInfo && (
        <div className="p-4 border border-gray-200 rounded-md bg-gray-50">
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-lg font-semibold text-brand-green-medium">Your Information</h4>
            <button onClick={() => goToStep(3)} className="text-sm text-brand-accent-orange hover:underline">Edit</button>
          </div>
          <p className="text-sm"><strong className="text-brand-text-light">Name:</strong> {stepThreeContactInfo.fullName}</p>
          <p className="text-sm"><strong className="text-brand-text-light">Phone:</strong> {stepThreeContactInfo.phone}</p>
          <p className="text-sm"><strong className="text-brand-text-light">Email:</strong> {stepThreeContactInfo.email}</p>
          <p className="text-sm"><strong className="text-brand-text-light">Address:</strong> {stepThreeContactInfo.streetAddress}{stepThreeContactInfo.aptSuite ? `, ${stepThreeContactInfo.aptSuite}` : ''}, {stepThreeContactInfo.city}, {stepThreeContactInfo.zipCode}</p>
        </div>
      )}

      {/* Preferred Schedule Section */}
      {stepFourSchedule && (
        <div className="p-4 border border-gray-200 rounded-md bg-gray-50">
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-lg font-semibold text-brand-green-medium">Preferred Schedule</h4>
            <button onClick={() => goToStep(4)} className="text-sm text-brand-accent-orange hover:underline">Edit</button>
          </div>
          <p className="text-sm"><strong className="text-brand-text-light">Date 1:</strong> {stepFourSchedule.preferredDate1 ? new Date(stepFourSchedule.preferredDate1 + 'T00:00:00').toLocaleDateString() : 'N/A'} ({stepFourSchedule.preferredTime1 || 'N/A'})</p>
          {stepFourSchedule.preferredDate2 && (
            <p className="text-sm"><strong className="text-brand-text-light">Date 2:</strong> {new Date(stepFourSchedule.preferredDate2 + 'T00:00:00').toLocaleDateString()} ({stepFourSchedule.preferredTime2 || 'N/A'})</p>
          )}
          {stepFourSchedule.schedulingNotes && <p className="text-sm mt-1"><strong className="text-brand-text-light">Notes:</strong> {stepFourSchedule.schedulingNotes}</p>}
        </div>
      )}
      
      <div className="mt-6 p-4 bg-yellow-50 border border-yellow-300 rounded-md">
          <p className="text-xs text-yellow-700">
              <strong>Important Disclaimer:</strong> This is an initial estimate based on your selections. Final costs may vary after technician assessment. Any fixed prices for selected add-ons will be honored. For installations, the 'Starting From' price is indicative and will be finalized after a site visit if necessary.
          </p>
      </div>

      {/* Consent Checkbox - if needed */}
      {/* <label className="flex items-center mt-6">
        <input type="checkbox" name="consent" className="h-5 w-5 text-brand-accent-orange border-gray-300 rounded focus:ring-brand-accent-orange mr-2" />
        <span className="text-sm text-brand-text-light">I acknowledge that I have read and agree to the terms.</span>
      </label> */}

      <div className="flex justify-between mt-8">
        <button type="button" onClick={onPrevious} className="cta-button-secondary">Previous</button>
        <button 
          type="button" 
          onClick={handleSubmit} 
          disabled={isSubmitting}
          className="cta-button disabled:opacity-50"
        >
          {isSubmitting ? 'Submitting...' : 'Confirm & Request Service'}
        </button>
      </div>
    </div>
  );
};

export default StepFiveReviewConfirm; 